// src/lib/save-data-guards.js — PR #146 BUG 2 fix
//
// Helpers extraits de src/App.jsx pour testabilité (cf. rapport pessimist deep
// docs/handoff/investigation-bug2-garde-cote-admin-2026-05-09.md).
// Logique inchangée — voir ADR-077 pour les seuils 50% et la genèse des 3 gardes.
//
// Les 3 gardes saveData :
//   1. Garde globale 50%   — refuse si nbMois ou nbResas chute >50%.
//   2. Garde per-mois      — refuse si UN mois précis passe de N>0 à 0 résas,
//                            mais reste présent comme clé (vidé sans suppression).
//   3. Garde SUM 50%       — refuse si count constant mais somme montants
//                            chute >50% (anti-écrasement montants par iCal).
//
// Exposées séparément + via evaluateSaveDataGuards qui retourne un verdict
// structuré { blocked, type, details } pour faciliter logs/Sentry/UI.

// Compte le nombre total de résas dans un blob fbz8-data (tous mois × tous logements).
export function countResasBlob(blob) {
  return Object.values(blob || {}).reduce(
    (s, mois) =>
      s +
      Object.values(mois || {}).reduce(
        (s2, entry) => s2 + (entry?.reservations || []).length,
        0,
      ),
    0,
  )
}

// Compte les résas pour un mois précis (sum sur tous logs).
export function countResasMois(blob, mk) {
  return Object.values(blob?.[mk] || {}).reduce(
    (s, entry) => s + (entry?.reservations || []).length,
    0,
  )
}

// Somme des montants (CA total) du blob. Protège contre le scénario "même
// nombre de résas mais montants tous écrasés à 0" qui passe la garde count
// (cf. ADR-077, incident 2026-05-09).
export function sumMontantsBlob(blob) {
  return Object.values(blob || {}).reduce(
    (s, mois) =>
      s +
      Object.values(mois || {}).reduce(
        (s2, entry) =>
          s2 +
          (entry?.reservations || []).reduce(
            (s3, r) => s3 + (parseFloat(r?.montant) || 0),
            0,
          ),
        0,
      ),
    0,
  )
}

// Évalue les 3 gardes sur (prevData → newData). Retourne :
//   { blocked: false } si OK
//   { blocked: true, type: 'global_50pct'|'per_mois'|'sum_50pct', details: {...} }
//
// L'ordre est identique à saveData (App.jsx) : globale, puis per-mois,
// puis SUM. Le premier verdict bloquant gagne.
export function evaluateSaveDataGuards(prevData, newData) {
  const prevMonths = Object.keys(prevData || {}).length
  const newMonths = Object.keys(newData || {}).length
  const prevResas = countResasBlob(prevData)
  const newResas = countResasBlob(newData)

  // 1. Garde globale 50% — refuse si nbMois ou nbResas chute de plus de 50%.
  if (
    prevMonths > 0 &&
    (newMonths < prevMonths * 0.5 || newResas < prevResas * 0.5)
  ) {
    return {
      blocked: true,
      type: 'global_50pct',
      details: {
        prevMonths,
        newMonths,
        prevResas,
        newResas,
        ratioMois: (newMonths / prevMonths).toFixed(2),
        ratioResas: prevResas > 0 ? (newResas / prevResas).toFixed(2) : 'n/a',
      },
    }
  }

  // 2. Garde per-mois — refuse si un mois passe de N>0 à 0 mais sa clé
  // reste présente dans newData (vidé sans suppression). Si la clé est
  // retirée intentionnellement, ne déclenche pas (la garde globale couvre).
  //
  // Skip les namespaces globaux racine préfixés `_` (_tombstones, _monthOverrides
  // Lot 3, …) — ce ne sont pas des mois (un mois est 'YYYY-MM').
  for (const mk of Object.keys(prevData || {})) {
    if (mk.startsWith('_')) continue
    const prev = countResasMois(prevData, mk)
    const next = countResasMois(newData, mk)
    if (prev > 0 && next === 0 && newData?.[mk]) {
      return {
        blocked: true,
        type: 'per_mois',
        details: { mk, prev, next, prevResas, newResas },
      }
    }
  }

  // 3. Garde SUM 50% — refuse si count constant mais montants chutent >50%.
  const prevSum = sumMontantsBlob(prevData)
  const newSum = sumMontantsBlob(newData)
  if (prevSum > 0 && newResas === prevResas && newSum < prevSum * 0.5) {
    return {
      blocked: true,
      type: 'sum_50pct',
      details: {
        prevResas,
        newResas,
        prevSum: prevSum.toFixed(2),
        newSum: newSum.toFixed(2),
        ratioSum: (newSum / prevSum).toFixed(2),
      },
    }
  }

  return { blocked: false }
}
